/**
 * testimonial-slider.js
 *
 * Native Testimonial Slider – Custom Element
 *
 * Architecture:
 *  - Extends HTMLElement (no external framework)
 *  - Uses the theme's Scroller class from @theme/scrolling for smooth
 *    scroll handling and event detection – no copy, no rebuild
 *  - CSS scroll-snap handles the actual snapping natively
 *  - Pagination dots are created dynamically in JS
 *  - All configurable values come from data-attributes written by the Liquid section
 *
 * @see assets/scrolling.js  – Scroller class & scrollIntoView helper
 * @see assets/slideshow.js  – Reference implementation (Slideshow component)
 */

import { Scroller } from '@theme/scrolling';

class TestimonialSliderComponent extends HTMLElement {
  // ─── Private state ────────────────────────────────────────────────
  /** @type {Scroller|null} */
  #scroller = null;

  /** @type {number} Currently active slide index */
  #currentIndex = 0;

  /** @type {number|undefined} Autoplay interval ID */
  #interval = undefined;

  /** @type {HTMLElement[]} All slide elements */
  #slides = [];

  /** @type {HTMLButtonElement[]} Dot buttons (created dynamically) */
  #dots = [];

  /** @type {ResizeObserver|null} */
  #resizeObserver = null;

  // ─── Lifecycle ────────────────────────────────────────────────────

  connectedCallback() {
    this.#slides = Array.from(this.querySelectorAll('.testimonial-slider__slide'));

    // Slider is disabled via setting – just display as static grid
    if (this.dataset.enableSlider !== 'true' || this.#slides.length <= 1) {
      this.#setupStatic();
      return;
    }

    this.#setup();
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  // ─── Initialisation ───────────────────────────────────────────────

  /** Set up the slider without interactive controls */
  #setupStatic() {
    // Ensure all slides are visible when slider is disabled
    for (const slide of this.#slides) {
      slide.setAttribute('aria-hidden', 'false');
    }
  }

  /** Full slider setup */
  #setup() {
    const scrollerEl = /** @type {HTMLElement|null} */ (this.querySelector('[data-scroller]'));
    if (!scrollerEl) return;

    // ── Scroller (from @theme/scrolling) ───────────────────────────
    // Handles scroll events, throttling, and the scroll-end promise.
    this.#scroller = new Scroller(scrollerEl, {
      onScroll: this.#handleScroll,
      onScrollEnd: this.#onScrollEnd,
    });

    // ── Touch / Mouse drag ─────────────────────────────────────────
    if (this.dataset.touch !== 'false') {
      // Native touch scrolling is already enabled by overflow-x: scroll.
      // We only need to optionally prevent page scroll while swiping horizontally.
      scrollerEl.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    }

    if (this.dataset.mouseDrag === 'true') {
      this.#setupMouseDrag(scrollerEl);
    }

    // ── Controls ───────────────────────────────────────────────────
    if (this.dataset.showArrows === 'true') {
      this.#setupArrows();
    }

    if (this.dataset.showDots === 'true') {
      this.#setupDots();
    }

    // ── Autoplay ───────────────────────────────────────────────────
    if (this.dataset.autoplay === 'true') {
      if (this.dataset.pauseOnHover === 'true') {
        this.addEventListener('mouseenter', this.#pauseAutoplay);
        this.addEventListener('mouseleave', this.#resumeAutoplay);
      }
      this.#startAutoplay();
    }

    // ── Responsive re-snap on resize ───────────────────────────────
    this.#resizeObserver = new ResizeObserver(() => {
      // Re-snap to the current slide after a layout recalculation
      const slide = this.#slides[this.#currentIndex];
      if (slide) {
        this.#scroller?.to(slide, { instant: true });
      }
    });
    this.#resizeObserver.observe(scrollerEl);

    // ── Initial state ──────────────────────────────────────────────
    this.#updateDots(0);
    this.#updateArrows(0);

    // Mark first slide as visible
    if (this.#slides[0]) {
      this.#slides[0].setAttribute('aria-hidden', 'false');
    }
  }

  // ─── Scroll handling ──────────────────────────────────────────────

  /**
   * Called by Scroller while the user is scrolling (throttled).
   * Syncs the active index to the most-visible slide.
   */
  #handleScroll = () => {
    const scrollerEl = this.querySelector('[data-scroller]');
    if (!scrollerEl || !this.#slides.length) return;

    const index = this.#nearestSlideIndex(scrollerEl);

    if (index !== this.#currentIndex) {
      this.#currentIndex = index;
      this.#updateDots(index);
      this.#updateArrows(index);
    }
  };

  /**
   * Called by Scroller after scrolling has ended.
   * Snaps exactly to the nearest slide to correct any drift.
   */
  #onScrollEnd = () => {
    const scrollerEl = this.querySelector('[data-scroller]');
    if (!scrollerEl || !this.#slides.length) return;

    const index = this.#nearestSlideIndex(scrollerEl);
    this.#currentIndex = index;
    this.#updateDots(index);
    this.#updateArrows(index);
  };

  /**
   * Determines the index of the slide closest to the scroll origin.
   * @param {HTMLElement} scrollerEl
   * @returns {number}
   */
  #nearestSlideIndex(scrollerEl) {
    const { scrollLeft } = scrollerEl;
    let closest = 0;
    let minDist = Infinity;

    this.#slides.forEach((slide, i) => {
      const dist = Math.abs(slide.offsetLeft - scrollLeft);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    });

    return closest;
  }

  // ─── Public navigation API ────────────────────────────────────────

  /**
   * Scrolls to a specific slide by index.
   * Uses Scroller.to() – same pattern as Slideshow.select().
   * @param {number} index
   */
  select(index) {
    if (!this.#slides.length || !this.#scroller) return;

    const infinite = this.dataset.infinite === 'true';
    let target = index;

    if (infinite) {
      if (target < 0) target = this.#slides.length - 1;
      if (target >= this.#slides.length) target = 0;
    } else {
      target = Math.max(0, Math.min(target, this.#slides.length - 1));
    }

    const slide = this.#slides[target];
    if (!slide) return;

    // scrollIntoView via Scroller.to() – no external lib, pure native scroll
    this.#scroller.to(slide);

    this.#currentIndex = target;
    this.#updateDots(target);
    this.#updateArrows(target);
  }

  /** Advance to the next slide */
  next() {
    this.select(this.#currentIndex + 1);
  }

  /** Go back to the previous slide */
  previous() {
    this.select(this.#currentIndex - 1);
  }

  // ─── Dot controls ─────────────────────────────────────────────────

  #setupDots() {
    const container = /** @type {HTMLElement|null} */ (this.querySelector('[data-dots-container]'));
    if (!container) return;

    // Clear any previously rendered dots (e.g., after theme editor reload)
    container.innerHTML = '';
    this.#dots = [];

    for (let i = 0; i < this.#slides.length; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'testimonial-slider__dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Slide ${i + 1}`);
      dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      // Dot colour uses --color-foreground (CSS variable from the active colour scheme)
      // No hardcoded colours – pure CSS handles the active / inactive states.
      dot.addEventListener('click', () => this.select(i));
      container.appendChild(dot);
      this.#dots.push(dot);
    }
  }

  /**
   * Updates aria-selected on all dots to reflect the active index.
   * The CSS rule [aria-selected='true'] applies --color-foreground as fill.
   * @param {number} index
   */
  #updateDots(index) {
    this.#dots.forEach((dot, i) => {
      dot.setAttribute('aria-selected', String(i === index));
    });
  }

  // ─── Arrow controls ───────────────────────────────────────────────

  #setupArrows() {
    const prev = /** @type {HTMLButtonElement|null} */ (this.querySelector('[data-prev]'));
    const next = /** @type {HTMLButtonElement|null} */ (this.querySelector('[data-next]'));

    prev?.addEventListener('click', () => this.previous());
    next?.addEventListener('click', () => this.next());
  }

  /**
   * Disables arrow buttons at boundaries when infinite loop is off.
   * @param {number} index
   */
  #updateArrows(index) {
    const infinite = this.dataset.infinite === 'true';
    const prev = /** @type {HTMLButtonElement|null} */ (this.querySelector('[data-prev]'));
    const next = /** @type {HTMLButtonElement|null} */ (this.querySelector('[data-next]'));

    if (prev) prev.disabled = !infinite && index === 0;
    if (next) next.disabled = !infinite && index >= this.#slides.length - 1;
  }

  // ─── Autoplay ─────────────────────────────────────────────────────

  #startAutoplay() {
    if (this.#interval !== undefined) return;

    const speed = parseInt(this.dataset.autoplaySpeed ?? '3000', 10);

    this.#interval = /** @type {number} */ (setInterval(() => {
      // Do not advance if the page/tab is hidden
      if (document.hidden) return;
      this.next();
    }, speed));
  }

  #pauseAutoplay = () => {
    clearInterval(this.#interval);
    this.#interval = undefined;
  };

  #resumeAutoplay = () => {
    if (this.dataset.autoplay === 'true') {
      this.#startAutoplay();
    }
  };

  // ─── Mouse drag ───────────────────────────────────────────────────

  /**
   * Enables click-and-drag scrolling on the track container.
   * @param {HTMLElement} scrollerEl
   */
  #setupMouseDrag(scrollerEl) {
    let isDown = false;
    let startX = 0;
    let scrollStart = 0;

    scrollerEl.addEventListener('mousedown', (e) => {
      isDown = true;
      startX = e.pageX;
      scrollStart = scrollerEl.scrollLeft;
      scrollerEl.style.cursor = 'grabbing';
      // Prevent text selection while dragging
      e.preventDefault();
    });

    const stopDrag = () => {
      if (!isDown) return;
      isDown = false;
      scrollerEl.style.cursor = '';
    };

    scrollerEl.addEventListener('mouseup', stopDrag);
    scrollerEl.addEventListener('mouseleave', stopDrag);

    scrollerEl.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      const dx = e.pageX - startX;
      scrollerEl.scrollLeft = scrollStart - dx;
    });
  }

  // ─── Touch (vertical scroll guard) ───────────────────────────────

  /** @type {number} */
  #touchStartX = 0;
  /** @type {number} */
  #touchStartY = 0;

  #handleTouchStart = (/** @type {TouchEvent} */ e) => {
    const touch = e.touches[0];
    if (!touch) return;
    this.#touchStartX = touch.clientX;
    this.#touchStartY = touch.clientY;
  };

  // ─── Cleanup ──────────────────────────────────────────────────────

  #cleanup() {
    // Stop autoplay timer – prevents memory leaks
    clearInterval(this.#interval);
    this.#interval = undefined;

    // Destroy Scroller (removes the scroll event listener)
    this.#scroller?.destroy();
    this.#scroller = null;

    // Disconnect ResizeObserver
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    // Remove hover listeners if they were attached
    if (this.dataset.pauseOnHover === 'true') {
      this.removeEventListener('mouseenter', this.#pauseAutoplay);
      this.removeEventListener('mouseleave', this.#resumeAutoplay);
    }
  }
}

// Register the custom element once
if (!customElements.get('testimonial-slider-component')) {
  customElements.define('testimonial-slider-component', TestimonialSliderComponent);
}
